import React, { useEffect, useState } from 'react';
import { Pivot, PivotItem, Spinner, PrimaryButton, DefaultButton } from '@fluentui/react';
import useAxios from 'axios-hooks';
import { Form, Formik } from 'formik';
import { useHistory, Prompt, useParams } from 'react-router-dom';
import { object, string, boolean } from 'yup';
import HomeHeroForm from './Forms/HomeHeroForm';
import HubImagesForm from './Forms/HubImagesForm';
import HomeOverviewForm from './Forms/HomeOverviewForm';
import SuccessComponent from '../../../Util/SuccessComponent';
import ErrorComponent from '../../../Util/ErrorComponent';
import SelectCobrandsForm from './Forms/SelectCobrandsForm';
import ShowCobrandList from './ShowCobrandList';
import ConfirmModal from './ConfirmModal';
import Features from './Forms/Features';
import FeatureSummary from "../../../Util/FeatureSummary";

const coBrandSettingsSchema = object({
    homeHeroHeader: string(),
    homeHeroHeaderAccent: string(),
    homeHeroHeaderEnd: string(),
    homeHeroSubheader: string().required(),
    homeHeroImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    homeOverviewCheckupImage1: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    homeOverviewPersonalityImage2: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    homeOverviewProgressImage3: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    hubTopicsImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    hubToolsImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    hubCoursesImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    hubMindfulnessImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    hubChallengeImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    showPlatformStatistics: boolean().required(),
    ympBannerImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    ympFeatureBlock1: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    ympFeatureBlock2: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    ympFeatureBlock3: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    stressScoreBannerImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    stressScoreFeatureBlock1: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    stressScoreFeatureBlock2: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    stressScoreFeatureBlock3: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    creditScoreBannerImage: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    creditScoreFeatureBlock1: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    creditScoreFeatureBlock2: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
    creditScoreFeatureBlock3: string().url().required().matches(/(https:)([/|.|\w|\s|-])*\.(?:webp)/, 'Image must start with https: and end with .webp'),
});

const newCoBrandSettingsObject = {
    client: '',
    cobrandTag: '',
    cobrand: '',
    homeHeroHeader: '',
    homeHeroHeaderAccent: '',
    homeHeroHeaderEnd: '',
    homeHeroSubheader: '',
    homeHeroImage: '',
    homeOverviewCheckupImage1: '',
    homeOverviewPersonalityImage2: '',
    homeOverviewProgressImage3: '',
    hubTopicsImage: '',
    hubToolsImage: '',
    hubCoursesImage: '',
    hubMindfulnessImage: '',
    hubChallengeImage: '',
    showPlatformStatistics: 0,
};

const hubSummary = [{
    selected: "1",
    label: "About Home Hero Section",
    summary:
        "Set the home hero section for the selected cobrand(s). This section appears at the top of the hub's home page and includes a header, subheader, and background image.",
    imageUrlPath: "hub-hero",
},
{
    selected: "2",
    label: "About Home Overview",
    summary:
        "Set the home overview section for the selected cobrand(s). This section provides an overview of the hub's features and offerings.",
    imageUrlPath: "hub-overview",
},
{
    selected: "3",
    label: "About Hub Images",
    summary:
        "Set the hero images for the selected cobrand(s) topics, tools, sweepstakes, and mindfulness hubs.",
    imageUrlPath: "hub-images",
},
{
    selected: "4",
    label: "About Hub Features",
    summary:
        "Set the featured content for the selected cobrand(s) YMP landing page, /personality.",
    imageUrlPath: "hub-features",
}]

const HubSettingsWizard = () => {
    const { coBrandID: coBrandIDFromParams } = useParams();
    const [cobrandString, setCobrandString] = useState('');
    const getFirstTab = () => {
        return coBrandIDFromParams ? 1 : 0;
    };
    const [selectedKey, setSelectedKey] = useState(getFirstTab());
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [formDirty, setFormDirty] = useState(false);
    const isBulkUpdate = !coBrandIDFromParams;
    const history = useHistory();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [totalToUpdate, setTotalToUpdate] = useState(0);

    const [{ data, loading: submitting }, submitForm] = useAxios(
        { url: `internal/cobrand/hubsettings` },
        { manual: true },
    );
    const [{ data: cobrandIdData, loading: fetchingCobrandIds }, getCobrandIds] = useAxios(
        { url: `internal/cobrand/ids-from-tags`, method: 'POST' },
        { manual: true },
    );

    const [{ data: cobrandHubData, loading: cobrandHubDataLoading }, getCobrandHubSettings] = useAxios(
        { url: `internal/cobrand/${coBrandIDFromParams}/hubsettings`, method: 'GET' },
        { manual: true },
    );
    const [{ data: cobrandData, loading: cobrandLoading }, getCobrandSettings] = useAxios(
        { url: `internal/clients/cobrands/settings/${cobrandString}`, method: 'GET' },
        { manual: true },
    );

    useEffect(() => {
        if (coBrandIDFromParams && coBrandIDFromParams !== 'bulk') {
            getCobrandHubSettings().then((res) => {
                setCobrandString(res.data[0]?.name || '');
            });
        }
    }, [coBrandIDFromParams]);

    useEffect(() => {
        if (cobrandString) {
            getCobrandSettings();
        }
    }, [cobrandString]);

    const handleLinkClick = (item) => {
        const itemKey = item.props.itemKey;
        setSelectedKey(Number(itemKey));
    };

    const handlePrevClick = () => setSelectedKey(Number(selectedKey) - 1);
    const handleNextClick = () => setSelectedKey(Number(selectedKey) + 1);

    const showFeaturesTab =
        cobrandData?.efxCreditScore || cobrandData?.stressScore || cobrandData?.yourMoneyPersonalityShow;

    if (cobrandHubDataLoading || submitting) {
        return <Spinner className="absolute-center" text="Loading Cobrand Hub Settings..." />;
    }

    return (
        <Formik
            initialValues={
                cobrandHubData?.length === 1
                    ? {
                          ...cobrandHubData[0],
                          showPlatformStatistics: cobrandHubData[0].showPlatformStatistics === true ? 1 : 0,
                      }
                    : newCoBrandSettingsObject
            }
            validationSchema={coBrandSettingsSchema}
            onSubmit={async (values) => {
                let result;
                setIsConfirmOpen(false);
                if (coBrandIDFromParams) {
                    result = await submitForm({
                        url: 'internal/cobrand/hubsettings',
                        method: 'PUT',
                        data: values,
                    });
                } else {
                    const getCobrandIdsData = {
                        client: values.client || null,
                        cobrand: values.cobrand || null,
                        cobrandTag: values.cobrandTag || null,
                    };
                    const CobrandIds = await getCobrandIds({
                        data: getCobrandIdsData,
                    });
                    const idListToUpdate = await CobrandIds.data.reduce((arr, current) => {
                        return [...arr, current.coBrandId];
                    }, []);
                    console.log(idListToUpdate);

                    result = await submitForm({
                        url: 'internal/cobrand/hubsettings',
                        method: 'POST',
                        data: { ...values, coBrandIDs: idListToUpdate },
                    });
                }

                if (!result.isAxiosError) {
                    setShowSuccess(true);
                    setTimeout(
                        () => history.push(`${window.location.host}/cobrand-settings-feature/hub-settings`),
                        2000,
                    );
                } else {
                    setShowError(true);
                }
            }}
            validateOnBlur
            isInitialValid={false}
        >
            {({ dirty, isValid, values, handleSubmit, setFieldValue }) => {
                const setDefaults = (site) => {
                    values.homeHeroHeader = `Reach your financial goals one step at a time`;
                    values.homeHeroSubheader = `Join ProductName, a personal finance program provided by CobrandDescription.`;
                    values.homeHeroImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/home-hero.webp`;
                    values.homeOverviewCheckupImage1 = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/overview-1.webp`;
                    values.homeOverviewPersonalityImage2 = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/overview-2.webp`;
                    values.homeOverviewProgressImage3 = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/overview-3.webp`;
                    values.hubChallengeImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/challenge.webp`;
                    values.hubCoursesImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/courses.webp`;
                    values.hubMindfulnessImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/mindfulness.webp`;
                    values.hubToolsImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/tools.webp`;
                    values.hubTopicsImage = `https://media-cdn.igrad.com/IMAGE/hub-redesign/${site}/topics.webp`;
                    values.ympBannerImage = `https://cdn.igrad.com/images/hero_banner/your-money-personality.webp`;
                    values.ympFeatureBlock1 = `https://cdn.igrad.com/images/ymp/landing-feature-1.webp`;
                    values.ympFeatureBlock2 = `https://cdn.igrad.com/images/ymp/landing-feature-2.webp`;
                    values.ympFeatureBlock3 = `https://cdn.igrad.com/images/ymp/landing-feature-3.webp`;
                    values.stressScoreBannerImage = `https://cdn.igrad.com/images/hero_banner/financial-stress.webp`;
                    values.stressScoreFeatureBlock1 = `https://cdn.igrad.com/images/stress-eval/landing-feature2-1.webp`;
                    values.stressScoreFeatureBlock2 = `https://cdn.igrad.com/images/stress-eval/landing-feature-2b.webp`;
                    values.stressScoreFeatureBlock3 = `https://cdn.igrad.com/images/stress-eval/landing-feature2-3.webp`;
                    values.creditScoreBannerImage = `https://cdn.igrad.com/images/hero_banner/credit-score-report.webp`;
                    values.creditScoreFeatureBlock1 = `https://images.igrad.com/credit_score/landing_page/cred_score.webp`;
                    values.creditScoreFeatureBlock2 = `https://images.igrad.com/credit_score/landing_page/typing.webp`;
                    values.creditScoreFeatureBlock3 = `https://images.igrad.com/credit_score/landing_page/checking-credit-score.webp`;
                    values.showPlatformStatistics = 0;
                    setFormDirty(true);
                    setRefresh((prev) => prev + 1);
                };

                const summaryObj = hubSummary.find(s => String(s.selected) === String(selectedKey));

                return (
                    <Form style={{ maxWidth: '800px' }}>
                        <Pivot selectedKey={String(selectedKey)} onLinkClick={handleLinkClick}>
                            {!coBrandIDFromParams && (
                                <PivotItem headerText="Select Cobrand" itemKey={'0'}>
                                    <SelectCobrandsForm />
                                </PivotItem>
                            )}

                            <PivotItem headerText="Home Hero Section" itemKey={'1'}>
                                <HomeHeroForm />
                            </PivotItem>

                            <PivotItem headerText="Home Overview" itemKey={'2'}>
                                <HomeOverviewForm />
                            </PivotItem>

                            <PivotItem headerText="Hub Images" itemKey={'3'}>
                                <HubImagesForm />
                            </PivotItem>

                            {(showFeaturesTab || isBulkUpdate) && (
                                <PivotItem headerText="Features" itemKey={'4'}>
                                    <Features cobrandData={cobrandData} isBulkUpdate={isBulkUpdate} />
                                </PivotItem>
                            )}
                        </Pivot>

                        <div style={{ width: '100%' }} className="mt-1">
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                {selectedKey === getFirstTab() ? (
                                    <div />
                                ) : (
                                    <DefaultButton text="Prev" onClick={handlePrevClick} />
                                )}
                                {selectedKey === 3 ? <div /> : <PrimaryButton text="Next" onClick={handleNextClick} />}
                            </div>
                            <div style={{ display: 'flex' }} className="mt-1">
                                <PrimaryButton
                                    onClick={() => setIsConfirmOpen(true)}
                                    text="Save All Fields"
                                    disabled={(!isValid || !dirty) && !formDirty}
                                />
                                <DefaultButton
                                    style={{ marginLeft: '10px' }}
                                    text="Set iGrad Defaults"
                                    onClick={() => setDefaults('igrad')}
                                />
                                <DefaultButton
                                    style={{ marginLeft: '10px' }}
                                    text="Set Enrich Defaults"
                                    onClick={() => setDefaults('enrich')}
                                />
                            </div>
                            <p className="mt-1" style={{ color: '#c8560f', fontSize: '18px' }}>
                                Caution! Using Set Defaults buttons will affect all fields on all tabs
                            </p>
                        </div>

                        {selectedKey === 0 && (
                            <ShowCobrandList
                                clients={values.client}
                                tags={values.cobrandTag}
                                cobrands={values.cobrand}
                                setTotalToUpdate={setTotalToUpdate}
                            />
                        )}

                        {summaryObj && (
                            <FeatureSummary
                                label={summaryObj.label}
                                summary={summaryObj.summary}
                                imageUrlPath={summaryObj.imageUrlPath}
                            />
                        )}

                        <ConfirmModal
                            isConfirmOpen={isConfirmOpen}
                            closeConfirmModal={() => setIsConfirmOpen(false)}
                            handleSubmit={handleSubmit}
                            totalToUpdate={totalToUpdate}
                        />

                        <SuccessComponent
                            successMessage="Hub Settings Updated."
                            showSuccess={showSuccess}
                            setShowSuccess={setShowSuccess}
                        />
                        <ErrorComponent
                            errorMessage="Errors in form, check all tabs for highlighted fields."
                            showError={showError}
                            setShowError={setShowError}
                        />
                        <div></div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default HubSettingsWizard;
